/**
 * Core Forum Utilities
 * 提供論壇相關的工具函數
 */

/**
 * 從當前節點追溯到根節點
 * @param {string|number} nodeId - 起始節點 ID
 * @param {Array} edges - 邊的陣列，支援兩種格式：
 *   - ReactFlow 格式：{ source, target }
 *   - 標準格式：{ from, to }
 * @param {Array} nodes - 節點的陣列，支援兩種格式：
 *   - ReactFlow 格式：{ id, data: { source: {...} } }
 *   - 標準格式：{ id, title, content, ... }
 * @returns {Array} 從當前節點到根節點的路徑（節點資料陣列）
 * 
 * @description
 * 在討論串中，邊的方向是：新節點（子）→ 舊節點（父）
 * - from/source = 新節點（子）
 * - to/target = 舊節點（父）
 * 
 * 此函數會從當前節點開始，沿著邊往前追溯，找出所有父節點直到根節點。
 * 返回的陣列順序為：[當前節點, 父節點, 祖父節點, ..., 根節點]
 */
export const traceToRoot = (nodeId, edges, nodes) => {
    const visited = new Set();
    const result = [];
    
    const startNodeId = String(nodeId);

    const dfs = (currentNodeId) => {
        const currentNodeIdStr = String(currentNodeId);
        
        if (visited.has(currentNodeIdStr)) {
            return;
        }
        visited.add(currentNodeIdStr);
        
        const node = nodes.find(n => String(n.id) === currentNodeIdStr);
        if (node) {
            // 支援兩種格式：
            // 1. ReactFlow 格式：{ id, data: { source: {...} } }
            // 2. 標準格式：直接就是節點數據 { id, title, content, ... }
            if (node.data && node.data.source) {
                result.push(node.data.source);
            } else if (!node.data || Object.keys(node.data).length === 0 || !node.data.source) {
                // 跳過沒有 data.source 的節點（無效節點）
                return;
            } else {
                result.push(node);
            }
        }

        // 找到所有 from/source = 當前節點的邊（即當前節點指向的父節點）
        // 支援兩種格式：{ from, to } 或 { source, target }
        const parentEdges = edges.filter(edge => {
            const childNode = edge.from || edge.source;
            return String(childNode) === currentNodeIdStr;
        });
        
        parentEdges.forEach(edge => {
            const parentNode = edge.to || edge.target;
            dfs(parentNode);
        });
    };

    dfs(startNodeId);
    
    return result;
};

/**
 * 構建對話歷史記錄（追溯路徑 + 時間排序的其他節點）
 * @param {string|number} currentNodeId - 當前節點 ID
 * @param {Array} edges - 邊的陣列
 * @param {Array} allNodes - 所有節點的陣列
 * @param {number} limit - 最多返回的節點數量
 * @returns {Array} 排序後的節點陣列
 * 
 * @description
 * 1. 先追溯當前節點到根節點的路徑
 * 2. 將其餘節點按時間排序
 * 3. 合併並限制數量
 */
export const buildConversationHistory = (currentNodeId, edges, allNodes, limit = 15) => {
    // 如果沒有指定當前節點，使用最新的節點
    if (!currentNodeId && allNodes.length > 0) {
        const sortedByTime = [...allNodes].sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
        );
        currentNodeId = sortedByTime[0]?.id;
    }
    
    if (!currentNodeId) {
        // 沒有節點，返回空陣列
        return [];
    }

    // 1. 追溯當前節點到根節點的路徑
    const tracedPath = traceToRoot(currentNodeId, edges, allNodes);
    const tracedIds = new Set(tracedPath.map(n => String(n.id)));

    // 2. 其餘節點按時間排序
    const otherNodes = allNodes
        .filter(n => !tracedIds.has(String(n.id)))
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    // 3. 合併：追溯路徑 + 時間排序的其他節點
    const combined = [...tracedPath, ...otherNodes];

    // 4. 限制數量
    return combined.slice(0, limit);
};

/**
 * 構建標籤論壇的對話歷史（追溯路徑 + 同標籤其他節點按時間排序）
 * @param {string|number} currentNodeId - 當前節點 ID
 * @param {Array} edges - 邊的陣列
 * @param {Array} allNodes - 所有節點的陣列
 * @param {string} currentTag - 當前節點的標籤
 * @param {number} limit - 最多返回的節點數量
 * @returns {Array} 排序後的節點陣列
 * 
 * @description
 * 1. 先追溯當前節點到根節點的路徑
 * 2. 將其餘「相同標籤」的節點按時間排序（新的在前）
 * 3. 合併並限制數量
 */
export const buildTaggedForumHistory = (currentNodeId, edges, allNodes, currentTag, limit = 15) => {
    // 如果沒有指定當前節點，使用該標籤下最新的節點
    if (!currentNodeId && allNodes.length > 0) {
        const nodesWithTag = allNodes.filter(n => n.tags === currentTag);
        const sortedByTime = [...nodesWithTag].sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
        );
        currentNodeId = sortedByTime[0]?.id;
    }
    
    if (!currentNodeId) {
        // 沒有節點，返回空陣列
        return [];
    }

    // 1. 追溯當前節點到根節點的路徑
    const tracedPath = traceToRoot(currentNodeId, edges, allNodes);
    const tracedIds = new Set(tracedPath.map(n => String(n.id)));

    // 2. 其餘「相同標籤」的節點按時間排序（新的在前）
    const otherNodes = allNodes
        .filter(n => n.tags === currentTag && !tracedIds.has(String(n.id)))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // 新的在前

    // 3. 合併：追溯路徑 + 時間排序的其他節點
    const combined = [...tracedPath, ...otherNodes];

    // 4. 限制數量
    return combined.slice(0, limit);
};

/**
 * 格式化節點歷史數據為文本
 * @param {Array} nodes - 節點陣列
 * @returns {string} 格式化後的歷史文本
 */
export const formatNodeHistory = (nodes) => {
    return nodes
        .map(node_data => {
            const createdAt = new Date(node_data.createdAt).toLocaleString();
            const content = node_data.title ? node_data.title.replace(/<[^>]*>/g, '').trim() : '';
            return `作者: ${node_data.author}\n時間: ${createdAt}\n標題: ${node_data.label}\n內容: ${content}\n`;
        })
        .join('\n---\n');
};
